/**
 * Who may do what inside the CRM (spec "Role matrix", crm-frontend-design.md §E.11).
 *
 * The SERVER enforces every row of this table (`backend/tenant_roles.py`,
 * `require_crm(role)`); the UI only hides what a role cannot use, so a helper is never
 * offered a button that answers 403 CRM_OWNER_ONLY.
 *
 * Unknown roles and unknown actions are refused: `can()` fails closed.
 */
export const ROLES = Object.freeze(["owner", "helper"]);

export const ROLE_MATRIX = Object.freeze({
  viewDashboard: { owner: true, helper: true },
  viewClients: { owner: true, helper: true },
  viewTimeline: { owner: true, helper: true },
  viewPurchases: { owner: true, helper: true },
  viewSessionNotes: { owner: true, helper: false },
  editClients: { owner: true, helper: true },
  exportAndViews: { owner: true, helper: true },
  manageEmail: { owner: true, helper: false },
  // crm-module BEGIN CR-T4-2 — distinct from `manageEmail` on purpose. The contract's
  // Roles section marks "bulk tags+stage+sequence" ALLOWED for a helper (front-desk
  // enrolling a walk-in into a sequence) in the SAME sentence it marks "all
  // /api/automation/*" (the sequence-EDITING admin surface) refused — two different
  // nouns the CrmClients.jsx call site used to collapse onto one key, hiding a button
  // the server (`POST /api/crm/clients/bulk {action:"add_to_sequence"}`, reached
  // through `require_crm("member", ...)`) already accepts.
  bulkSequence: { owner: true, helper: true },
  // crm-module END
  managePacks: { owner: true, helper: false },
  assignPack: { owner: true, helper: true },
  mergeEraseDelete: { owner: true, helper: false },
  promoteBilling: { owner: true, helper: false },
  // crm-module BEGIN C3.02 — Team (lane F5): invite/list/resend/remove is owner-only
  // (the spec's Team block); a helper cannot manage who else has access.
  team: { owner: true, helper: false },
  // crm-module END
  // crm-module BEGIN CR-F3-1 — the CRM's own rules (`/api/crm/settings` is owner-only).
  settings: { owner: true, helper: false },
  // crm-module END
});

/** The role a session is treated as when the server states none. */
export const FALLBACK_ROLE = "helper";

/** Normalises a wire role; anything unknown is the least-privileged role. */
export const normaliseRole = (value) => (ROLES.includes(value) ? value : FALLBACK_ROLE);

/** @returns {boolean} whether `role` may perform `action`. */
export const can = (role, action) => ROLE_MATRIX[action]?.[normaliseRole(role)] === true;
